import { Flex, Icon, Tr } from '@chakra-ui/react';
import { useGenericContext } from 'components/contexts/useGenericContext';
import { H1, Modal } from 'components/elements';
import { KYBMoralUpdateForm } from 'components/forms';
import { IKYBMoralUpdateFormInitialValues } from 'components/forms/KYBMoralUpdateForm/types.d';
import { Td, TdActions, TdBin, TdTag } from 'components/lib';
import { CustomTable } from 'components/modules';
import {
  addErrorToast,
  addSuccessToast,
} from 'containers/ToastManager/store/slice';
import { FormikHelpers } from 'formik';
import { getId, getResults, isResultsEmpty } from 'helpers';
import fp from 'lodash/fp';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { RiFileEditFill } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useToggle } from 'react-use';
import { KYBMoralResources } from 'services/resources/users/kyb-moral';
import {
  IKYBMoralPersonPayload,
  IKYBMoralPersonResource,
} from 'services/resources/users/kyb-moral/types.d';

import {
  getApplicationType,
  getCreated,
  getIsAccredited,
  getIsKybCompleted,
  getLegalRepresentative,
  getLegalRepresentativeEmail,
  getTradeName,
  HEADERS,
} from '../helpers';

export const Page: React.FC = (): JSX.Element => {
  const {
    active,
    defaultRestore,
    registry,
    setRegistry,
    onPrepareDelete,
    state,
  } = useGenericContext<IKYBMoralPersonResource>();

  const dispatch = useDispatch();

  const [isOpen, toggle] = useToggle(false);

  const history = useHistory();

  const { path } = useRouteMatch();

  const { t } = useTranslation('kyb-moral');

  const handleOnSubmit = async (
    v: IKYBMoralUpdateFormInitialValues,
    helpers?: FormikHelpers<IKYBMoralUpdateFormInitialValues>,
  ) => {
    const {
      address_proof: ap,
      constitutive_certificate: cc,
      electronic_sign_number_proof: esnp,
      legal_representative_ID: lri,
      legal_representative_proof: lrp,
      official_id_number_proof: oinp,
      signed_statement: ss,
      tax_id_proof: tip,
    } = v;
    try {
      const payload = new FormData();
      if (!fp.isNil(cc))
        payload.append(
          'constitutive_certificate',
          cc as File,
          (cc as File).name,
        );
      if (!fp.isNil(esnp))
        payload.append(
          'electronic_sign_number_proof',
          esnp as File,
          (esnp as File).name,
        );
      if (!fp.isNil(lri))
        payload.append(
          'legal_representative_ID',
          lri as File,
          (lri as File).name,
        );
      if (!fp.isNil(lrp))
        payload.append(
          'legal_representative_proof',
          lrp as File,
          (lrp as File).name,
        );
      if (!fp.isNil(ap))
        payload.append('address_proof', ap as File, (ap as File).name);
      if (!fp.isNil(oinp)) {
        payload.append(
          'official_id_number_proof',
          oinp as File,
          (oinp as File).name,
        );
      }
      if (!fp.isNil(ss)) {
        payload.append('signed_statement', ss as File, (ss as File).name);
      }
      if (!fp.isNil(tip)) {
        payload.append('tax_id_proof', tip as File, (tip as File).name);
      }
      await KYBMoralResources.updateFiles(registry.id, payload);
      dispatch(addSuccessToast('toast.success.files'));
      toggle();
    } catch (err) {
      dispatch(addErrorToast('toast.errors.files.network'));
    } finally {
      helpers?.setSubmitting(false);
    }
  };

  return (
    <>
      <CustomTable
        isEmpty={isResultsEmpty(state)}
        headers={HEADERS}
        isLoading={state?.loading}
        mx={8}
      >
        {getResults(state)?.map((row: IKYBMoralPersonResource) => (
          <Tr key={nanoid()} position="relative">
            <Td>{getId(row)}</Td>
            <Td>{getTradeName(row)}</Td>
            <TdTag>{getApplicationType(row)}</TdTag>
            <Td>{getLegalRepresentative(row)}</Td>
            <Td>{getLegalRepresentativeEmail(row)}</Td>
            <TdBin isCheck={getIsKybCompleted(row)} />
            <TdBin isCheck={getIsAccredited(row)} />
            <Td>{getCreated(row)}</Td>
            <TdActions
              customButtonBg="orange"
              customButtonIcon={<Icon as={RiFileEditFill} color="white" />}
              isActive={active}
              onCustomCallback={() => {
                setRegistry(row);
                toggle();
              }}
              onDelete={() => onPrepareDelete(row)}
              onRestore={() => defaultRestore(row, { is_active: true })}
              onUpdate={() => history.push(`${path}/${row?.id}`)}
            />
          </Tr>
        ))}
      </CustomTable>

      <Modal isOpen={isOpen} onClose={toggle}>
        <Flex
          direction="column"
          h="100%"
          justify="center"
          maxW="512px"
          mx="auto"
        >
          <H1 mb={12}>{t('modal.title')}</H1>
          <KYBMoralUpdateForm
            initialValues={{
              is_active: true,
              is_accredited: true,
              address_proof: undefined,
              constitutive_certificate: undefined,
              electronic_sign_number_proof: undefined,
              legal_representative_ID: undefined,
              legal_representative_proof: undefined,
              official_id_number_proof: undefined,
              signed_statement: undefined,
              tax_id_proof: undefined,
            }}
            onSubmit={handleOnSubmit}
          />
        </Flex>
      </Modal>
    </>
  );
};
